import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { pioneerBranches, pioneerRecharge, pioneerRechargeOptions, stbPkgids } from "../../api/pioneerApi";
import AddonPicker from "./AddonPicker";
import { MONTH_DAYS, fmtDate, parseDate, periodOf, rupees, selectedConflicts } from "./packRules";
import "../Combo/combo.css";
import "./pioneer.css";

const errText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

/**
 * Pioneer IPTV recharge of one set-top box: one DPO (FTA included), optional A-la-carte /
 * Broadcaster add-ons, 1 / 3 / 6 / 12 months of 30 days. The whole amount is taken once.
 * Advance renewal: the box's current packs are renewed as they are — only the months are chosen.
 *
 * Props: open, onClose, customer ({ id, operatorId }), stb (Pioneer STB row), advance (bool),
 *        onRecharged(responseData)
 */
const PioneerRechargeModal = ({ open, onClose, customer, stb, advance, onRecharged }) => {
  const [opts, setOpts] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [dpoId, setDpoId] = useState(null);
  const [addonIds, setAddonIds] = useState([]);
  const [months, setMonths] = useState(1);
  const [dpoQuery, setDpoQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── load everything when opened ─────────────────────────
  useEffect(() => {
    if (!open || !customer?.id || !stb) return undefined;
    let cancelled = false;
    setOpts(null);
    setLoadError("");
    setAddonIds([]);
    setMonths(1);
    setDpoQuery("");
    setBranchId("");
    (async () => {
      try {
        const [o, b] = await Promise.all([
          pioneerRechargeOptions(customer.id, advance ? { currentPkgids: stbPkgids(stb) } : {}),
          pioneerBranches(customer.operatorId).catch(() => ({ data: { branches: [] } })),
        ]);
        if (cancelled) return;
        const data = o.data;
        setOpts(data);
        const list = b.data?.branches || [];
        setBranches(list);
        if (list.length === 1) setBranchId(String(list[0].id));

        // pre-select what the box has now: its DPO and add-ons (Pioneer pkgid = pack id)
        const current = new Set((stb.plans || []).map((p) => Number(p.pkgid)));
        const dpo = data.dpos.find((d) => current.has(d.pioneer_id)) || data.dpos[0];
        setDpoId(dpo ? dpo.id : null);
        const addons = [...(data.addons["A-la-carte"] || []), ...(data.addons.Broadcaster || [])];
        setAddonIds(addons.filter((a) => current.has(a.pioneer_id)).map((a) => a.id));
      } catch (err) {
        if (!cancelled) setLoadError(errText(err, "Could not load the IPTV packs"));
      }
    })();
    return () => { cancelled = true; };
  }, [open, customer?.id, customer?.operatorId, stb, advance]);

  const dpo = opts?.dpos.find((d) => d.id === dpoId) || null;
  const basePacks = useMemo(() => [dpo, opts?.fta].filter(Boolean), [dpo, opts]);
  const allAddons = useMemo(
    () => (opts ? [...(opts.addons["A-la-carte"] || []), ...(opts.addons.Broadcaster || [])] : []),
    [opts]
  );
  const chosen = addonIds.map((id) => allAddons.find((a) => a.id === id)).filter(Boolean);
  const conflicts = useMemo(() => selectedConflicts(basePacks, chosen), [basePacks, chosen]);

  const shownDpos = useMemo(() => {
    const words = dpoQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = opts?.dpos || [];
    return words.length ? list.filter((d) => words.every((w) => d.name.toLowerCase().includes(w))) : list;
  }, [opts, dpoQuery]);

  const current = advance ? opts?.current || null : null;
  const addonsMonthly = chosen.reduce((s, a) => s + Number(a.price || 0), 0);
  const monthly = advance
    ? Number(current?.monthly || 0)
    : (dpo ? Number(dpo.price || 0) : 0) + addonsMonthly;
  const total = Math.round(monthly * months * 100) / 100;
  const [from, till] = periodOf(months, advance, stb?.expiration);
  const paidTill = stb && opts?.paid_till ? opts.paid_till[stb.stb_box] : null;
  const wallet = typeof opts?.wallet === "number" ? opts.wallet : null;

  const problems = [];
  if (opts && advance) {
    if (!current || current.problem) problems.push(current?.problem || "The box's current packs could not be read.");
  } else if (opts) {
    if (!opts.dpos.length) problems.push("No DPO package is assigned to this operator.");
    else if (!dpo) problems.push("Choose a DPO package.");
    if (conflicts.size) problems.push("Remove the add-ons marked in red — their channels are already included.");
  }
  if (opts) {
    if (!branches.length) problems.push("No Pioneer IPTV branch is assigned to this operator.");
    else if (!branchId) problems.push("Choose the branch.");
    if (paidTill) problems.push(`This set-top box is already paid till ${fmtDate(parseDate(paidTill))}.`);
    if (wallet !== null && total > wallet) problems.push(`Wallet balance ${rupees(wallet)} is less than ${rupees(total)}.`);
  }

  const submit = async () => {
    if (submitting || problems.length || (!advance && !dpo)) return;
    setSubmitting(true);
    try {
      const { data } = await pioneerRecharge({
        user_id: customer.id,
        device_id: stb.stb_box,
        branch_id: Number(branchId),
        // advance renewal: the server renews the box's current packs (read live from Pioneer)
        plan_id: advance ? undefined : dpo.id,
        addon_ids: advance ? [] : addonIds,
        months,
        advance: !!advance,
        current_expiry: stb.expiration,
      });
      if (!data.success) throw new Error(data.error || "Recharge failed");
      const till2 = parseDate(data.paid_till);
      toast.success(`${advance ? "Advance recharge" : "Recharge"} successful${till2 ? ` — valid till ${fmtDate(till2)}` : ""}`);
      if (onRecharged) onRecharged(data);
      onClose();
    } catch (err) {
      toast.error(errText(err, "Recharge failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !stb) return null;
  const expiry = parseDate(stb.expiration);
  const active = expiry && expiry >= new Date();

  return (
    <div className="cmb-overlay" role="dialog" aria-modal="true" aria-label="Pioneer IPTV recharge">
      <div className="cmb-modal piptv-modal">
        <div className="cmb-modal__head">
          <div>
            <h3>{advance ? "Advance recharge" : "Recharge"} · Pioneer IPTV</h3>
            <div className="cmb-hint">
              Set-top box <strong className="piptv-mono">{stb.stb_box}</strong>
              {expiry ? (active ? ` · active till ${fmtDate(expiry)}` : ` · expired ${fmtDate(expiry)}`) : ""}
            </div>
          </div>
          <button type="button" className="cmb-modal__close" onClick={onClose} disabled={submitting} aria-label="Close">×</button>
        </div>

        <div className="cmb-modal__body piptv-body">
          {loadError ? (
            <div className="cmb-note cmb-note--danger">{loadError}</div>
          ) : !opts ? (
            <div className="piptv-loading"><span className="card-loading-spinner" /> Loading packs…</div>
          ) : (
            <div className="piptv-grid">
              {/* ── left: packs ── */}
              <div className="piptv-col">
                {advance ? (
                  <section>
                    <div className="piptv-sec-head">
                      <h4>Packs on this box</h4>
                      <span className="piptv-fta">Renewed as they are</span>
                    </div>
                    {!current ? (
                      <div className="cmb-note cmb-note--warn">The box's current packs could not be read.</div>
                    ) : current.problem ? (
                      <div className="cmb-note cmb-note--warn">{current.problem}</div>
                    ) : (
                      <div className="piptv-list piptv-list--dpo">
                        <div className="piptv-row is-on">
                          <span className="piptv-row__main">
                            <span className="piptv-row__name">{current.dpo.name}</span>
                            <span className="piptv-row__sub">DPO · FTA included</span>
                          </span>
                          <span className="piptv-row__price">{rupees(current.dpo.price)}<small> / month</small></span>
                        </div>
                        {current.addons.map((a) => (
                          <div key={a.id} className="piptv-row">
                            <span className="piptv-row__main">
                              <span className="piptv-row__name">{a.name}</span>
                              <span className="piptv-row__sub">{a.type === "Broadcaster" ? "Broadcaster pack" : "A-la-carte channel"}</span>
                            </span>
                            <span className="piptv-row__price">{rupees(a.price)}<small> / month</small></span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="cmb-hint" style={{ marginTop: 8 }}>
                      An advance renewal keeps the box's current package and add-ons. To change packs, recharge the box
                      after it expires.
                    </p>
                  </section>
                ) : (
                  <>
                <section>
                  <div className="piptv-sec-head">
                    <h4>DPO package</h4>
                    <span className="piptv-fta">FTA included</span>
                  </div>
                  {opts.dpos.length > 5 ? (
                    <div className="piptv-search">
                      <span aria-hidden="true">⌕</span>
                      <input type="text" value={dpoQuery} onChange={(e) => setDpoQuery(e.target.value)} placeholder="Search DPO packages…" />
                      {dpoQuery ? <button type="button" className="piptv-search__clear" onClick={() => setDpoQuery("")} aria-label="Clear search">×</button> : null}
                    </div>
                  ) : null}
                  <div className="piptv-list piptv-list--dpo">
                    {shownDpos.length === 0 ? (
                      <div className="piptv-empty">{opts.dpos.length ? `Nothing matches “${dpoQuery.trim()}”.` : "No DPO packages are assigned to this operator."}</div>
                    ) : shownDpos.map((d) => (
                      <label key={d.id} className={`piptv-row ${d.id === dpoId ? "is-on" : ""}`}>
                        <input type="radio" name="piptv-dpo" checked={d.id === dpoId} onChange={() => setDpoId(d.id)} disabled={submitting} />
                        <span className="piptv-row__main">
                          <span className="piptv-row__name">{d.name}</span>
                          <span className="piptv-row__sub">
                            {d.channel_count ? `${d.channel_count} ${d.channel_count === 1 ? "channel" : "channels"} · ` : ""}FTA included
                          </span>
                        </span>
                        <span className="piptv-row__price">{rupees(d.price)}<small> / month</small></span>
                      </label>
                    ))}
                  </div>
                </section>

                <section>
                  <div className="piptv-sec-head">
                    <h4>Add-ons <span className="piptv-optional">(optional)</span></h4>
                  </div>
                  <p className="cmb-hint piptv-tight">
                    Channels already in the DPO, FTA or another add-on can't be added again.
                  </p>
                  <AddonPicker
                    addons={opts.addons}
                    selectedIds={addonIds}
                    onChange={setAddonIds}
                    basePacks={basePacks}
                    conflicts={conflicts}
                    months={months}
                    disabled={submitting}
                  />
                </section>
                                </>
                )}
</div>

              {/* ── right: period + summary ── */}
              <div className="piptv-col piptv-col--side">
                <section>
                  <h4>Period</h4>
                  <div className="piptv-months" role="radiogroup" aria-label="Months">
                    {(opts.months || [1, 3, 6, 12]).map((m) => (
                      <button
                        key={m}
                        type="button"
                        role="radio"
                        aria-checked={months === m}
                        className={`piptv-month ${months === m ? "is-on" : ""}`}
                        onClick={() => setMonths(m)}
                        disabled={submitting}
                      >
                        <strong>{m}</strong>
                        <span>{m === 1 ? "month" : "months"}</span>
                        <small>{m * (opts.month_days || MONTH_DAYS)} days</small>
                      </button>
                    ))}
                  </div>
                </section>

                {branches.length > 1 ? (
                  <div className="cmb-field">
                    <label htmlFor="piptv-branch">Branch</label>
                    <select id="piptv-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={submitting}>
                      <option value="">Select branch</option>
                      {branches.map((b) => <option key={b.id} value={b.id}>{b.display_name}</option>)}
                    </select>
                  </div>
                ) : branches.length === 1 ? (
                  <div className="cmb-hint">Branch: <strong>{branches[0].display_name}</strong></div>
                ) : null}

                <section className="piptv-summary">
                  <h4>Summary</h4>
                  {(advance ? current && current.dpo : dpo) ? (
                    <>
                      <div className="piptv-line"><span>{(advance ? current.dpo : dpo).name}</span><span>{rupees((advance ? current.dpo : dpo).price)}</span></div>
                      {opts.fta ? <div className="piptv-line piptv-line--muted"><span>{opts.fta.name}</span><span>Included</span></div> : null}
                      {(advance ? current.addons : chosen).map((a) => (
                        <div key={a.id} className={`piptv-line ${conflicts.has(a.id) ? "piptv-line--bad" : ""}`}>
                          <span>{a.name}</span><span>{rupees(a.price)}</span>
                        </div>
                      ))}
                      <div className="piptv-line piptv-line--sub"><span>Per month</span><span>{rupees(monthly)}</span></div>
                      {months > 1 ? <div className="piptv-line piptv-line--muted"><span>× {months} months</span><span /></div> : null}
                      <div className="piptv-line piptv-line--total"><span>Total</span><span>{rupees(total)}</span></div>
                      <div className="piptv-validity">
                        Valid <strong>{fmtDate(from)}</strong> – <strong>{fmtDate(till)}</strong>
                        <span> ({months * (opts.month_days || MONTH_DAYS)} days)</span>
                      </div>
                      {wallet !== null ? <div className="cmb-hint">Wallet balance {rupees(wallet)}</div> : null}
                    </>
                  ) : (
                    <div className="cmb-hint">Choose a DPO package.</div>
                  )}
                </section>

                {problems.length ? (
                  <div className="cmb-note cmb-note--warn">{problems.map((p) => <div key={p}>{p}</div>)}</div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        <div className="cmb-modal__foot">
          <button type="button" className="cmb-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className="cmb-btn cmb-btn--primary"
            onClick={submit}
            disabled={!opts || submitting || problems.length > 0}
          >
            {submitting ? "Recharging…" : `Pay ${rupees(total)} & recharge`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PioneerRechargeModal;
